import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    CreatePurchaseInNoteRequest,
    CreatePurchaseOutNoteRequest,
    CreateWithdrawalNoteRequest,
    ListPurchaseLotsParams,
    ListPurchaseNotesParams,
    ListWithdrawalNotesParams,
    UpdatePurchaseInNoteRequest,
    UpdatePurchaseOutNoteRequest,
} from '@/lib/api/purchase-lot-api'
import { purchaseLotApi } from '@/lib/api/purchase-lot-api'
import { teaPurchaseKeys } from '@/hooks/use-tea-purchase-query'
import { m } from '@/lib/paraglide/messages'

export const purchaseLotKeys = {
    all: ['purchase-lots'] as const,
    lots: () => [...purchaseLotKeys.all, 'lots'] as const,
    lotList: (params: ListPurchaseLotsParams) =>
        [...purchaseLotKeys.lots(), 'list', params] as const,
    lotDetail: (id: string) =>
        [...purchaseLotKeys.lots(), 'detail', id] as const,
    inNotes: () => [...purchaseLotKeys.all, 'in-notes'] as const,
    inNoteList: (params: ListPurchaseNotesParams) =>
        [...purchaseLotKeys.inNotes(), 'list', params] as const,
    inNoteDetail: (id: string) =>
        [...purchaseLotKeys.inNotes(), 'detail', id] as const,
    outNotes: () => [...purchaseLotKeys.all, 'out-notes'] as const,
    outNoteList: (params: ListPurchaseNotesParams) =>
        [...purchaseLotKeys.outNotes(), 'list', params] as const,
    outNoteDetail: (id: string) =>
        [...purchaseLotKeys.outNotes(), 'detail', id] as const,
    withdrawalNotes: () =>
        [...purchaseLotKeys.all, 'withdrawal-notes'] as const,
    withdrawalNoteList: (params: ListWithdrawalNotesParams) =>
        [...purchaseLotKeys.withdrawalNotes(), 'list', params] as const,
    withdrawalNoteDetail: (id: string) =>
        [...purchaseLotKeys.withdrawalNotes(), 'detail', id] as const,

    outNotesForSorting: (search: string) =>
        [...purchaseLotKeys.outNotes(), 'for-sorting', search] as const,
}

export function usePurchaseLots(
    params: ListPurchaseLotsParams = {},
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: purchaseLotKeys.lotList(params),
        queryFn: () => purchaseLotApi.listLots(params),
    })
}

/**
 * Infinite variant cho mobile "Xem thêm" pattern. Accumulate pages 1..N.
 * `enabled` false-safe: khi caller pass false (vd viewport không phải mobile),
 * hook không fetch.
 */
export function usePurchaseLotsInfinite(
    params: Omit<ListPurchaseLotsParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [...purchaseLotKeys.lots(), 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            purchaseLotApi.listLots({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function usePurchaseLot(id: string | undefined) {
    return useQuery({
        enabled: !!id,
        queryKey: purchaseLotKeys.lotDetail(id ?? ''),
        queryFn: () => purchaseLotApi.getLot(id as string),
    })
}

export function usePurchaseLotHistory(id: string | undefined) {
    return useInfiniteQuery({
        enabled: !!id,
        queryKey: [...purchaseLotKeys.lots(), 'history', id ?? ''] as const,
        queryFn: ({ pageParam }) =>
            purchaseLotApi.getLotHistory(id as string, {
                page: pageParam,
                per_page: 20,
            }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function usePurchaseInNotes(
    params: ListPurchaseNotesParams = {},
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: purchaseLotKeys.inNoteList(params),
        queryFn: () => purchaseLotApi.listInNotes(params),
    })
}

/**
 * Infinite variant cho mobile "Xem thêm" pattern. Accumulate pages 1..N.
 * `enabled` false-safe: khi caller pass false (vd viewport không phải mobile),
 * hook không fetch.
 */
export function usePurchaseInNotesInfinite(
    params: Omit<ListPurchaseNotesParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [...purchaseLotKeys.inNotes(), 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            purchaseLotApi.listInNotes({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function usePurchaseInNote(id: string | undefined) {
    return useQuery({
        enabled: !!id,
        queryKey: purchaseLotKeys.inNoteDetail(id ?? ''),
        queryFn: () => purchaseLotApi.getInNote(id as string),
    })
}

export function useCreatePurchaseInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (req: CreatePurchaseInNoteRequest) =>
            purchaseLotApi.createInNote(req),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: purchaseLotKeys.inNotes() })
            qc.invalidateQueries({ queryKey: purchaseLotKeys.lots() })
            // Tạo PNK from_purchase chiếm đơn mua → cập nhật list has_active_pnk.
            qc.invalidateQueries({ queryKey: teaPurchaseKeys.all })
            toast.success(m.pl_toast_create_in_note())
        },
    })
}

export function useUpdatePurchaseInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            req,
        }: {
            id: string
            req: UpdatePurchaseInNoteRequest
        }) => purchaseLotApi.updateInNote(id, req),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({ queryKey: purchaseLotKeys.inNoteDetail(id) })
            qc.invalidateQueries({ queryKey: purchaseLotKeys.inNotes() })
            qc.invalidateQueries({ queryKey: purchaseLotKeys.lots() })
            // Đổi đơn mua nguồn (from_purchase) → cập nhật list has_active_pnk.
            qc.invalidateQueries({ queryKey: teaPurchaseKeys.all })
            toast.success(m.pl_toast_update_in_note())
        },
    })
}

export function useApprovePurchaseInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => purchaseLotApi.approveInNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({ queryKey: purchaseLotKeys.inNoteDetail(id) })
            qc.invalidateQueries({ queryKey: purchaseLotKeys.inNotes() })
            qc.invalidateQueries({ queryKey: purchaseLotKeys.lots() })
            toast.success(m.pl_toast_approve_in_note())
        },
    })
}

export function useRejectPurchaseInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, reason }: { id: string; reason: string }) =>
            purchaseLotApi.rejectInNote(id, reason),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({ queryKey: purchaseLotKeys.inNoteDetail(id) })
            qc.invalidateQueries({ queryKey: purchaseLotKeys.inNotes() })
            qc.invalidateQueries({ queryKey: teaPurchaseKeys.all })
            toast.success(m.pl_toast_reject_in_note())
        },
    })
}

export function useCancelPurchaseInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => purchaseLotApi.cancelInNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({ queryKey: purchaseLotKeys.inNoteDetail(id) })
            qc.invalidateQueries({ queryKey: purchaseLotKeys.inNotes() })
            qc.invalidateQueries({ queryKey: teaPurchaseKeys.all })
            toast.success(m.pl_toast_cancel_in_note())
        },
    })
}

/**
 * PXK lô chè mua đã duyệt, lý do "đưa đi phân loại" — nguồn hợp lệ cho PNK lô
 * phân loại. Dùng cho picker ở form tạo PNK.
 */
export function usePurchaseOutNotesForSorting(
    search = '',
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: purchaseLotKeys.outNotesForSorting(search),
        queryFn: () =>
            purchaseLotApi.listOutNotesForSorting(search || undefined),
    })
}

export function usePurchaseOutNotes(
    params: ListPurchaseNotesParams = {},
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: purchaseLotKeys.outNoteList(params),
        queryFn: () => purchaseLotApi.listOutNotes(params),
    })
}

/**
 * Infinite variant cho mobile "Xem thêm" pattern. Accumulate pages 1..N.
 * `enabled` false-safe: khi caller pass false (vd viewport không phải mobile),
 * hook không fetch.
 */
export function usePurchaseOutNotesInfinite(
    params: Omit<ListPurchaseNotesParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [...purchaseLotKeys.outNotes(), 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            purchaseLotApi.listOutNotes({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function usePurchaseOutNote(id: string | undefined) {
    return useQuery({
        enabled: !!id,
        queryKey: purchaseLotKeys.outNoteDetail(id ?? ''),
        queryFn: () => purchaseLotApi.getOutNote(id as string),
    })
}

export function useCreatePurchaseOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (req: CreatePurchaseOutNoteRequest) =>
            purchaseLotApi.createOutNote(req),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: purchaseLotKeys.outNotes() })
            toast.success(m.pl_toast_create_out_note())
        },
    })
}

export function useUpdatePurchaseOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            req,
        }: {
            id: string
            req: UpdatePurchaseOutNoteRequest
        }) => purchaseLotApi.updateOutNote(id, req),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({
                queryKey: purchaseLotKeys.outNoteDetail(id),
            })
            qc.invalidateQueries({ queryKey: purchaseLotKeys.outNotes() })
            qc.invalidateQueries({ queryKey: purchaseLotKeys.lots() })
            toast.success(m.pl_toast_update_out_note())
        },
    })
}

export function useApprovePurchaseOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => purchaseLotApi.approveOutNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({
                queryKey: purchaseLotKeys.outNoteDetail(id),
            })
            qc.invalidateQueries({ queryKey: purchaseLotKeys.outNotes() })
            qc.invalidateQueries({ queryKey: purchaseLotKeys.lots() })
            toast.success(m.pl_toast_approve_out_note())
        },
    })
}

export function useRejectPurchaseOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, reason }: { id: string; reason: string }) =>
            purchaseLotApi.rejectOutNote(id, reason),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({
                queryKey: purchaseLotKeys.outNoteDetail(id),
            })
            qc.invalidateQueries({ queryKey: purchaseLotKeys.outNotes() })
            toast.success(m.pl_toast_reject_out_note())
        },
    })
}

export function useCancelPurchaseOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => purchaseLotApi.cancelOutNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({
                queryKey: purchaseLotKeys.outNoteDetail(id),
            })
            qc.invalidateQueries({ queryKey: purchaseLotKeys.outNotes() })
            toast.success(m.pl_toast_cancel_out_note())
        },
    })
}

// ============================================================================
// Phiếu lấy hàng (withdrawal note) queries
// ============================================================================

export function usePurchaseWithdrawalNotes(
    params: ListWithdrawalNotesParams = {},
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: purchaseLotKeys.withdrawalNoteList(params),
        queryFn: () => purchaseLotApi.listWithdrawalNotes(params),
    })
}

/** Infinite variant cho mobile "Xem thêm" pattern. */
export function usePurchaseWithdrawalNotesInfinite(
    params: Omit<ListWithdrawalNotesParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [
            ...purchaseLotKeys.withdrawalNotes(),
            'infinite',
            params,
        ] as const,
        queryFn: ({ pageParam }) =>
            purchaseLotApi.listWithdrawalNotes({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function usePurchaseWithdrawalNote(id: string | undefined) {
    return useQuery({
        enabled: !!id,
        queryKey: purchaseLotKeys.withdrawalNoteDetail(id ?? ''),
        queryFn: () => purchaseLotApi.getWithdrawalNote(id as string),
    })
}

export function useCreatePurchaseWithdrawalNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (req: CreateWithdrawalNoteRequest) =>
            purchaseLotApi.createWithdrawalNote(req),
        onSuccess: () => {
            qc.invalidateQueries({
                queryKey: purchaseLotKeys.withdrawalNotes(),
            })
            toast.success(m.pl_toast_create_plh())
        },
    })
}

export function useCancelPurchaseWithdrawalNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => purchaseLotApi.cancelWithdrawalNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({
                queryKey: purchaseLotKeys.withdrawalNoteDetail(id),
            })
            qc.invalidateQueries({
                queryKey: purchaseLotKeys.withdrawalNotes(),
            })
            toast.success(m.pl_toast_cancel_plh())
        },
    })
}
